import React, { useState, useEffect, useCallback } from 'react';
import {
  Plus, MessageSquare, Users, Clock, Loader2, Scale, CheckCircle2,
  AlertTriangle, Zap, Minus, Search
} from 'lucide-react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { getNeeds } from '../api';
import { HiringNeed, User, URGENCY_LABELS, NEED_STATUS_LABELS, NeedUrgency, NeedStatus } from '../types';

interface Props {
  currentUser: User;
  onSelect: (id: string) => void;
  onCreateNew: () => void;
  refreshKey?: number;
  notificationBell?: React.ReactNode;
}

const statusColors: Record<string, string> = {
  debating: 'bg-purple-50 text-purple-600 border-purple-200',
  discussing: 'bg-blue-50 text-blue-600 border-blue-200',
  confirmed: 'bg-green-50 text-green-600 border-green-200',
  cancelled: 'bg-gray-50 text-gray-500 border-gray-200',
};

const urgencyIcons: Record<string, React.ReactNode> = {
  low: <Minus size={12} />,
  normal: <Clock size={12} />,
  high: <AlertTriangle size={12} />,
  urgent: <Zap size={12} />,
};

const urgencyColors: Record<string, string> = {
  low: 'text-gray-400',
  normal: 'text-blue-500',
  high: 'text-orange-500',
  urgent: 'text-red-500',
};

export default function NeedsList({ currentUser, onSelect, onCreateNew, refreshKey, notificationBell }: Props) {
  const [needs, setNeeds] = useState<HiringNeed[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | NeedStatus>('all');
  const [search, setSearch] = useState('');

  const fetchNeeds = useCallback(async () => {
    try {
      const data = await getNeeds();
      setNeeds(data);
    } catch {
      toast.error('加载需求列表失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchNeeds(); }, [fetchNeeds, refreshKey]);

  const filtered = needs.filter(n => {
    if (filter !== 'all' && n.status !== filter) return false;
    if (search.trim()) {
      const q = search.toLowerCase();
      return (n.department || '').toLowerCase().includes(q)
        || (n.business_problem || '').toLowerCase().includes(q)
        || (n.jd_title || '').toLowerCase().includes(q)
        || (n.creator_name || '').toLowerCase().includes(q);
    }
    return true;
  });

  const statusCounts = {
    all: needs.length,
    debating: needs.filter(n => n.status === 'debating').length,
    discussing: needs.filter(n => n.status === 'discussing').length,
    confirmed: needs.filter(n => n.status === 'confirmed').length,
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header */}
      <div className="shrink-0 bg-white border-b border-gray-200 px-4 md:px-6 pt-4 pb-0">
        <div className="flex items-center justify-between mb-3">
          <div className="pl-10 md:pl-0">
            <h1 className="text-lg font-bold text-gray-900">需求发起</h1>
            <p className="text-sm text-gray-400 mt-0.5">提出岗位需求 → AI辩论分析 → 讨论确认 → 自动创建岗位</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onCreateNew}
              className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors shadow-sm">
              <Plus size={16} />
              <span className="hidden md:inline">新建需求</span>
            </button>
            {notificationBell}
          </div>
        </div>

        {/* Search */}
        <div className="relative mb-3">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder="搜索部门、需求、岗位名..."
            className="w-full h-9 pl-9 pr-3 rounded-lg border border-gray-200 bg-gray-50 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 focus:bg-white transition-all"
          />
        </div>

        {/* Filter tabs */}
        <div className="flex gap-1 overflow-x-auto no-scrollbar pb-0">
          {([
            { key: 'all', label: '全部', count: statusCounts.all },
            { key: 'debating', label: 'AI辩论中', count: statusCounts.debating },
            { key: 'discussing', label: '讨论中', count: statusCounts.discussing },
            { key: 'confirmed', label: '已确认', count: statusCounts.confirmed },
          ] as { key: typeof filter; label: string; count: number }[]).map(t => (
            <button key={t.key} onClick={() => setFilter(t.key)}
              className={clsx(
                'px-3 py-2 text-xs font-medium border-b-2 whitespace-nowrap transition-colors',
                filter === t.key ? 'text-primary-600 border-primary-600' : 'text-gray-400 border-transparent hover:text-gray-600'
              )}
            >
              {t.label}
              <span className={clsx(
                'ml-1.5 px-1.5 py-0.5 rounded-full text-[10px]',
                filter === t.key ? 'bg-primary-50 text-primary-600' : 'bg-gray-100 text-gray-400'
              )}>{t.count}</span>
            </button>
          ))}
        </div>
      </div>

      {/* List */}
      {filtered.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center text-gray-400">
          <Scale size={48} strokeWidth={1} className="text-gray-300" />
          <p className="mt-3 text-sm">
            {needs.length === 0 ? '还没有需求，点击右上角创建第一个' : '没有匹配的需求'}
          </p>
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto p-3 md:p-4">
          <div className="space-y-2.5">
            {filtered.map(n => (
              <div
                key={n.id}
                onClick={() => onSelect(n.id)}
                className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md hover:border-primary-200 px-4 py-3.5 cursor-pointer transition-all"
              >
                <div className="flex items-start gap-3">
                  {/* Icon */}
                  <div className={clsx(
                    'shrink-0 w-10 h-10 rounded-xl flex items-center justify-center',
                    n.status === 'debating' ? 'bg-purple-100' :
                    n.status === 'discussing' ? 'bg-blue-100' :
                    n.status === 'confirmed' ? 'bg-green-100' : 'bg-gray-100'
                  )}>
                    {n.status === 'debating' ? <Scale size={18} className="text-purple-500" /> :
                     n.status === 'confirmed' ? <CheckCircle2 size={18} className="text-green-500" /> :
                     <MessageSquare size={18} className="text-blue-500" />}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-sm font-bold text-gray-900 truncate">
                        {n.jd_title || n.business_problem?.substring(0, 30) || '新需求'}
                      </h3>
                      <span className={clsx('px-1.5 py-0.5 rounded-full text-[10px] font-medium border', statusColors[n.status])}>
                        {NEED_STATUS_LABELS[n.status as NeedStatus]}
                      </span>
                    </div>

                    <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                      {n.business_problem}
                    </p>

                    <div className="flex items-center gap-3 mt-2 text-[11px] text-gray-400">
                      {n.department && <span>{n.department}</span>}
                      {n.need_source && <span className="px-1.5 py-0.5 bg-orange-50 text-orange-500 rounded text-[10px]">{n.need_source}</span>}
                      <span className="flex items-center gap-0.5">
                        <Users size={11} />{n.member_count || 0}人
                      </span>
                      <span className="flex items-center gap-0.5">
                        <MessageSquare size={11} />{n.comment_count || 0}
                      </span>
                      <span className={clsx('flex items-center gap-0.5', urgencyColors[n.urgency])}>
                        {urgencyIcons[n.urgency]}
                        {URGENCY_LABELS[n.urgency as NeedUrgency]}
                      </span>
                      <span className="ml-auto">{n.creator_name}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
